import { useRef, useEffect } from 'react';

/* Vídeo del hero que se "reproduce" con el scroll mientras el hero está pineado.
   El progreso se calcula de forma determinista a partir de la posición del hero:
   progreso 0 cuando el hero está arriba del todo, 1 cuando se ha desplazado
   (altura_hero − viewport) px → justo cuando el sticky se despinea.
   Así el glaseado termina exactamente al acabar el pin. */
export default function HeroVideo({ src, poster, track, className, style }) {
  const vid = useRef(null);
  const durRef = useRef(0);

  useEffect(() => {
    const el = vid.current;
    const sec = track && track.current;
    if (!el || !sec) return;

    const onMeta = () => { durRef.current = el.duration || 0; update(); };
    el.addEventListener('loadedmetadata', onMeta);
    if (el.readyState >= 1) durRef.current = el.duration || 0;
    el.load();

    let raf = null;
    const update = () => {
      raf = null;
      const d = durRef.current;
      if (!d) return;
      const dist = sec.offsetHeight - window.innerHeight; // distancia de pin en px
      const rectTop = sec.getBoundingClientRect().top;
      const p = dist > 0 ? Math.min(1, Math.max(0, -rectTop / dist)) : 0;
      const t = Math.min(d - 0.04, p * d);
      if (Math.abs(el.currentTime - t) > 0.012) {
        try { el.currentTime = t; } catch {}
      }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();

    return () => {
      el.removeEventListener('loadedmetadata', onMeta);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [track]);

  return (
    <video
      ref={vid}
      className={className}
      style={style}
      src={src}
      poster={poster}
      muted
      playsInline
      preload="auto"
      tabIndex={-1}
      aria-hidden="true"
    />
  );
}
